import React from 'react';
import { ShoppingCart } from 'lucide-react';
import Lottie from 'lottie-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import cartLoadingAnimation from '../assets/cart-loading.json';

export const ProductCard = ({ product, merchant }) => {
  const { t } = useLanguage();
  const { addItem, loadingProducts } = useCart();
  const isLoading = loadingProducts.has(product.product_id);

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    try {
      await addItem(product, merchant);
      toast.success(`${product.product_name} ${t('add_to_cart')}`);
    } catch (error) {
      toast.error('Gagal menambahkan ke keranjang');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID').format(price);
  };

  return (
    <div
      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden group"
      data-testid={`product-card-${product.product_id}`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        <img
          src={product.img_url || 'https://via.placeholder.com/200x200?text=No+Image'}
          alt={product.product_name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/200x200?text=No+Image';
          }}
        />
        {product.terjual > 0 && (
          <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium text-gray-700">
            {t('sold')} {product.terjual}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Category */}
        <span className="text-xs text-[#0052CC] font-medium uppercase tracking-wide">
          {product.category}
        </span>

        {/* Product Name */}
        <h3 className="font-medium text-gray-900 mt-1 line-clamp-2 text-sm min-h-[2.5rem]" data-testid="product-name">
          {product.product_name}
        </h3>

        {/* Price */}
        <div className="mt-2 flex items-baseline gap-1">
          <span className="text-lg font-bold text-[#0052CC]" data-testid="product-price">
            Rp{formatPrice(product.price)}
          </span>
          <span className="text-xs text-gray-500">/{product.unit}</span>
        </div>

        {/* Store */}
        <p className="text-xs text-gray-500 mt-1 truncate">
          {t('from')} {merchant.store_name}
        </p>

        {/* Add to Cart Button */}
        <Button
          onClick={handleAddToCart}
          disabled={isLoading}
          className="w-full mt-3 bg-[#0052CC] hover:bg-[#0043A8] text-white rounded-lg py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
          data-testid={`add-to-cart-${product.product_id}`}
        >
          {isLoading ? (
            <>
              <Lottie
                animationData={cartLoadingAnimation}
                loop={true}
                style={{ width: 20, height: 20 }}
              />
              <span>Menambahkan...</span>
            </>
          ) : (
            <>
              <ShoppingCart className="w-4 h-4" />
              {t('add_to_cart')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ProductCard;
